import React, { useState } from 'react';
import {
  MonitorPlay,
  PlusCircle,
  Link as LinkIcon,
  ExternalLink,
  BarChart,
  MessageCircle,
  AlertTriangle,
} from 'lucide-react';
import { WizardData } from '../components/PublishingWizard';
import CardComponent from '../ui/CardComponent';
import ButtonComponent from '../ui/ButtonComponent';
import BadgeComponent from '../ui/BadgeComponent';

interface WizardStep5Props {
  data: WizardData;
  updateData: (data: Partial<WizardData>) => void;
}

const END_SCREEN_TEMPLATES = [
  { id: 'subscribe', name: 'Subscribe + Video', preview: 'Standard' },
  { id: 'videos', name: 'Best for Viewer', preview: 'Recommended' },
  { id: 'playlist', name: 'Playlist Focus', preview: 'Collection' },
];

const CARD_TYPES = [
  { id: 'video', name: 'Video', icon: MonitorPlay },
  { id: 'playlist', name: 'Playlist', icon: BarChart },
  { id: 'link', name: 'Link', icon: LinkIcon },
  { id: 'poll', name: 'Poll', icon: MessageCircle },
];

export default function WizardStep5({ data, updateData }: WizardStep5Props) {
  const [showAddCard, setShowAddCard] = useState(false);
  const [selectedEndScreen, setSelectedEndScreen] = useState('');

  const handleAddCard = (type: string) => {
    const newCard = {
      type,
      title: `New ${type.charAt(0).toUpperCase() + type.slice(1)}`,
    };
    updateData({
      cards: [...data.cards, newCard],
    });
    setShowAddCard(false);
  };

  const handleRemoveCard = (index: number) => {
    const cards = data.cards.filter((_, i) => i !== index);
    updateData({ cards });
  };

  const handleEndScreenToggle = (templateId: string) => {
    setSelectedEndScreen(templateId);
    updateData({ endScreenEnabled: true });
  };

  return (
    <div className="space-y-8">
      {/* Info Banner */}
      <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <AlertTriangle size={18} className="text-blue-700 flex-shrink-0 mt-0.5" />
        <div>
          <div className="text-sm font-medium text-blue-900 mb-1">Video Elements Help Increase Engagement</div>
          <div className="text-xs text-blue-700">
            Add end screens and cards to promote other content and keep viewers watching. These features are only
            available for video content.
          </div>
        </div>
      </div>

      {/* End Screens */}
      <CardComponent padding="none">
        <div className="p-6 border-b border-[#E5E7EB]">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-[#111827]">End Screens</h3>
              <p className="text-xs text-[#6B7280] mt-1">Promote your content in the last 5-20 seconds of your video</p>
            </div>
            <BadgeComponent variant={data.endScreenEnabled ? 'success' : 'default'}>
              {data.endScreenEnabled ? 'Enabled' : 'Disabled'}
            </BadgeComponent>
          </div>
        </div>
        <div className="p-6">
          {!data.endScreenEnabled ? (
            <div className="space-y-4">
              <p className="text-sm text-[#6B7280] mb-4">Select a template to get started:</p>
              <div className="grid grid-cols-3 gap-4">
                {END_SCREEN_TEMPLATES.map((template) => (
                  <button
                    key={template.id}
                    onClick={() => handleEndScreenToggle(template.id)}
                    className="p-4 border-2 border-[#E5E7EB] rounded-lg hover:border-[#2563EB] hover:bg-[#EFF6FF] transition-all text-center"
                  >
                    <div className="w-full h-24 bg-[#F3F4F6] rounded mb-3 flex items-center justify-center">
                      <MonitorPlay size={32} className="text-[#9CA3AF]" />
                    </div>
                    <div className="text-sm font-medium text-[#111827]">{template.name}</div>
                    <div className="text-xs text-[#6B7280] mt-1">{template.preview}</div>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="p-6 bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg">
                <div className="flex items-center justify-between mb-4">
                  <div className="text-sm font-medium text-[#111827]">End Screen Preview</div>
                  <ButtonComponent variant="ghost" size="sm" onClick={() => updateData({ endScreenEnabled: false })}>
                    Remove
                  </ButtonComponent>
                </div>
                <div className="w-full h-32 bg-[#E5E7EB] rounded flex items-center justify-center">
                  <MonitorPlay size={48} className="text-[#9CA3AF]" />
                </div>
                <div className="mt-4 grid grid-cols-2 gap-3">
                  <div className="p-3 bg-white border border-[#E5E7EB] rounded">
                    <div className="text-xs text-[#6B7280] mb-1">Subscribe button</div>
                    <div className="text-sm font-medium text-[#111827]">Enabled</div>
                  </div>
                  <div className="p-3 bg-white border border-[#E5E7EB] rounded">
                    <div className="text-xs text-[#6B7280] mb-1">Video element</div>
                    <div className="text-sm font-medium text-[#111827]">Best for viewer</div>
                  </div>
                </div>
              </div>
              <ButtonComponent variant="secondary" size="sm">
                Edit End Screen
              </ButtonComponent>
            </div>
          )}
        </div>
      </CardComponent>

      {/* Cards */}
      <CardComponent padding="none">
        <div className="p-6 border-b border-[#E5E7EB]">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-[#111827]">Cards</h3>
              <p className="text-xs text-[#6B7280] mt-1">Add interactive cards that appear during your video</p>
            </div>
            <ButtonComponent variant="secondary" size="sm" onClick={() => setShowAddCard(!showAddCard)}>
              <PlusCircle size={16} className="mr-2" />
              Add CardComponent
            </ButtonComponent>
          </div>
        </div>
        <div className="p-6">
          {data.cards.length === 0 ? (
            <div className="text-center py-8">
              <PlusCircle size={32} className="mx-auto text-[#9CA3AF] mb-3" />
              <p className="text-sm text-[#6B7280] mb-4">
                No cards added yet. Cards can promote videos, playlists, channels, or polls.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {data.cards.map((card, index) => (
                <div key={index} className="flex items-center justify-between p-4 border border-[#E5E7EB] rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-[#F9FAFB] border border-[#E5E7EB] rounded flex items-center justify-center">
                      {card.type === 'video' && <MonitorPlay size={16} className="text-[#6B7280]" />}
                      {card.type === 'link' && <LinkIcon size={16} className="text-[#6B7280]" />}
                      {card.type === 'poll' && <MessageCircle size={16} className="text-[#6B7280]" />}
                    </div>
                    <div>
                      <div className="text-sm font-medium text-[#111827]">{card.title}</div>
                      <div className="text-xs text-[#6B7280] capitalize">{card.type} card</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <ButtonComponent variant="ghost" size="sm">
                      Edit
                    </ButtonComponent>
                    <ButtonComponent variant="ghost" size="sm" onClick={() => handleRemoveCard(index)}>
                      Remove
                    </ButtonComponent>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Add CardComponent Panel */}
          {showAddCard && (
            <div className="mt-4 p-4 border-2 border-[#2563EB] bg-[#EFF6FF] rounded-lg">
              <div className="text-sm font-medium text-[#111827] mb-3">Select CardComponent Type:</div>
              <div className="grid grid-cols-2 gap-3">
                {CARD_TYPES.map((type) => {
                  const Icon = type.icon;
                  return (
                    <button
                      key={type.id}
                      onClick={() => handleAddCard(type.id)}
                      className="p-4 bg-white border border-[#E5E7EB] rounded-lg hover:border-[#2563EB] hover:shadow-sm transition-all"
                    >
                      <Icon size={24} className="mx-auto mb-2 text-[#6B7280]" />
                      <div className="text-sm font-medium text-[#111827]">{type.name}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </CardComponent>

      {/* Subtitles Editor */}
      <CardComponent padding="none">
        <div className="p-6 border-b border-[#E5E7EB]">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-[#111827]">Subtitles Editor</h3>
              <p className="text-xs text-[#6B7280] mt-1">Add or edit subtitles for your video</p>
            </div>
            <BadgeComponent variant={data.subtitlesFile ? 'success' : 'default'}>
              {data.subtitlesFile ? 'Uploaded' : 'None'}
            </BadgeComponent>
          </div>
        </div>
        <div className="p-6">
          <div className="p-6 border border-[#E5E7EB] rounded-lg bg-[#F9FAFB]">
            <div className="text-sm font-medium text-[#111827] mb-2">Transcript Preview</div>
            <div className="text-xs text-[#6B7280] mb-4">
              {data.subtitlesEnabled
                ? 'Auto-generated subtitles will be created when the video is uploaded.'
                : 'Enable subtitles in the Compliance step to auto-generate captions.'}
            </div>
            <div className="flex gap-3">
              <ButtonComponent variant="secondary" size="sm">
                Upload SRT/VTT
              </ButtonComponent>
              <ButtonComponent variant="ghost" size="sm">
                Edit Subtitles
              </ButtonComponent>
            </div>
          </div>
        </div>
      </CardComponent>

      {/* External Links Safety Check */}
      {data.platforms.some((p) => ['Instagram', 'Facebook'].includes(p)) && (
        <CardComponent padding="none">
          <div className="p-6 border-b border-[#E5E7EB]">
            <div className="flex items-center gap-2">
              <ExternalLink size={18} className="text-[#6B7280]" />
              <h3 className="text-sm font-medium text-[#111827]">External Links Safety</h3>
            </div>
          </div>
          <div className="p-6">
            <div className="flex items-start gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <AlertTriangle size={18} className="text-yellow-700 flex-shrink-0 mt-0.5" />
              <div>
                <div className="text-sm font-medium text-yellow-900 mb-1">
                  Link Safety Check for Instagram & Facebook
                </div>
                <div className="text-xs text-yellow-700">
                  External links will be checked against platform policies. Banned or restricted URLs may prevent
                  publishing.
                </div>
              </div>
            </div>
            <div className="mt-4 space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-[#6B7280]">URLs detected</span>
                <span className="font-medium text-[#111827]">0</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-[#6B7280]">Banned URLs</span>
                <span className="font-medium text-green-600">0</span>
              </div>
            </div>
          </div>
        </CardComponent>
      )}
    </div>
  );
}
